import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from 'environments/environment';
import type { ApiHeaders, ApiParams, ApiRequestOptions, ApiResource } from '../../types';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);

  get<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.get<T>(this.url(path, options?.resource), this.requestOptions(options));
  }

  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.post<T>(this.url(path, options?.resource), body ?? null, this.requestOptions(options));
  }

  put<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.put<T>(this.url(path, options?.resource), body ?? null, this.requestOptions(options));
  }

  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Observable<T> {
    return this.http.patch<T>(this.url(path, options?.resource), body ?? null, this.requestOptions(options));
  }

  delete<T>(path: string, options?: ApiRequestOptions): Observable<T> {
    return this.http.delete<T>(this.url(path, options?.resource), this.requestOptions(options));
  }

  postBlob(path: string, body?: unknown, options?: ApiRequestOptions): Observable<Blob> {
    return this.http.post(this.url(path, options?.resource), body ?? null, {
      headers: this.buildHeaders(options?.headers),
      params: this.buildParams(options?.params),
      context: options?.context,
      withCredentials: options?.withCredentials ?? false,
      observe: 'body',
      responseType: 'blob',
    });
  }

  private url(path: string, resource?: ApiResource): string {
    if (path.startsWith("https://") || path.startsWith("http://")) return path;
    if (path.startsWith("/")) path = path.slice(1);
    return `https://${resource}.${environment.apiDomain}/${path}`;
  }

  private requestOptions(options?: ApiRequestOptions) {
    return {
      headers: this.buildHeaders(options?.headers),
      params: this.buildParams(options?.params),
      context: options?.context,
      withCredentials: options?.withCredentials ?? false,
      observe: 'body' as const,
      responseType: 'json' as const,
    };
  }

  private buildHeaders(custom?: ApiHeaders): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    if (!custom) return headers;
    for (const [key, value] of Object.entries(custom)) {
      headers = headers.set(key, value);
    }
    return headers;
  }

  private buildParams(custom?: ApiParams): HttpParams {
    let params = new HttpParams();
    if (!custom) return params;
    for (const [key, value] of Object.entries(custom)) {
      if (value === null || value === undefined) continue;
      if (Array.isArray(value)) {
        for (const item of value) params = params.append(key, String(item));
      } else {
        params = params.set(key, String(value));
      }
    }
    return params;
  }
}
